import { useEffect, useState } from "react";
import { Search, Plus, RefreshCw, Edit, Trash2, X, AlertCircle, UserPlus, Users, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter, Eye, EyeOff, Download, CheckCircle, XCircle, Mail, Printer, Pencil, Briefcase } from "lucide-react";
import apiService from "../config";
import DownloadModal from "../Components/Download/DownloadModal";
import { toast } from "react-toastify";
import { useLanguage } from "../StoreContext/LanguageContext";

const UserAndRoleManagement = () => {

    const { language, translations } = useLanguage();
    const [projectId, setProjectId] = useState(null);
    const [projectName, setProjectName] = useState(null);
    const [proposalId, setProposalId] = useState(null);
    const [subUsers, setSubUsers] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'add' or 'edit'
    const [currentUser, setCurrentUser] = useState(null);
    const [workAssignments, setWorkAssignments] = useState([]);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        mobile: '',
        usr_pwd: '',
        confirmPassword: '',
        work_assign: []
    });
    const [formErrors, setFormErrors] = useState({});
    const [loadingSubUsers, setLoadingSubUsers] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Project Id
    useEffect(() => {
        const getProjectInfo = () => {
            const currentProjectData = localStorage.getItem('currentProjectData');
            if (currentProjectData) {
                try {
                    const projectData = JSON.parse(currentProjectData);
                    return {
                        id: projectData.project_id || projectData.id,
                        name: projectData.project_name || projectData.name
                    };
                } catch (e) {
                    console.error('Error parsing current project data (currentProjectData):', e);
                }
            }

            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return {
                        id: project.project_id || project.id,
                        name: project.project_name || project.name
                    };
                } catch (e) {
                    console.error('Error parsing selected project (selectedProject):', e);
                }
            }

            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    const projectId = user.data?.project_id || user.project_id;
                    const projectName = user.data?.project_name || user.project_name || 'Unknown Project';
                    return {
                        id: projectId,
                        name: projectName
                    };
                } catch (e) {
                    console.error('Error parsing user data (userData):', e);
                }
            }
            return { id: null, name: null };
        };

        const projectInfo = getProjectInfo();
        setProjectId(projectInfo.id);
        setProjectName(projectInfo.name);
        console.log('Current Project Info:', projectInfo);
    }, []);

    // Proposal id
    useEffect(() => {
        const getProposalInfo = () => {
            const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');

            if (currentProposal) {
                try {
                    return {
                        proposalId: currentProposal?.data?.default_proposal ||
                            currentProposal?.data?.proposal_id ||
                            currentProposal?.default_proposal ||
                            currentProposal?.proposal_id ||
                            null
                    }
                } catch (e) {
                    console.error('Error parsing current project data (getProposalInfo):', e);
                }
            }
            return { proposalId: null };
        };

        const proposalInfo = getProposalInfo();
        setProposalId(proposalInfo.proposalId);
        console.log('Current Project-Proposal Info:', proposalInfo);
    }, []);

    useEffect(() => {
        getSubUsers();
    }, []);

    const getSubUsers = async () => {
        setLoadingSubUsers(true);
        try {
            const response = await apiService.getAllSubUsers();
            if (response.status === 200 && response.data) {
                setSubUsers(response.data);
            } else {
                console.log("Failed to load subUsers");
            }
        } catch (error) {
            console.log("Error fetching subUsers");
        } finally {
            setLoadingSubUsers(false);
        }
    };

    useEffect(() => {
        fetchWorkAssignments();
    }, []);

    const fetchWorkAssignments = async () => {
        try {
            const result = await apiService.getWorkAssign();
            if (result && result.status === 200 && result.data) {
                setWorkAssignments(result.data);
            }
        } catch (err) {
            console.error("fetchWorkAssignments:", err);
        }
    };

    // Validation function
    const validateForm = () => {
        const errors = {};

        if (!formData.full_name.trim()) {
            errors.full_name = 'Name is required';
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            errors.email = 'Invalid email format';
        }

        const mobileRegex = /^[0-9]{10}$/;
        if (!formData.mobile.trim()) {
            errors.mobile = 'Mobile number is required';
        } else if (!mobileRegex.test(formData.mobile)) {
            errors.mobile = 'Mobile number must be 10 digits';
        }

        if (modalType === 'add') {
            if (!formData.usr_pwd) {
                errors.usr_pwd = 'Password is required';
            } else if (formData.usr_pwd.length < 6) {
                errors.usr_pwd = 'Password must be at least 6 characters';
            }

            if (!formData.confirmPassword) {
                errors.confirmPassword = 'Confirm password is required';
            } else if (formData.usr_pwd !== formData.confirmPassword) {
                errors.confirmPassword = 'Passwords do not match';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: value
            };
            return updated;
        });

        // Clear error for this field without causing re-render
        if (formErrors[name]) {
            setFormErrors(prev => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });
        }
    };

    // Handle work assignment checkbox
    const handleWorkAssignChange = (workId) => {
        setFormData(prev => ({
            ...prev,
            work_assign: prev.work_assign.includes(workId)
                ? prev.work_assign.filter(id => id !== workId)
                : [...prev.work_assign, workId]
        }));
    };

    // Open modal for add/edit
    const openModal = (type, user = null) => {
        setModalType(type);
        setCurrentUser(user);

        if (type === 'edit' && user) {
            setFormData({
                full_name: user.full_name || '',
                email: user.email || '',
                mobile: user.mobile || '',
                usr_pwd: '',
                confirmPassword: '',
                work_assign: user.work_assign ? user.work_assign.split(',').map(id => parseInt(id)) : []
            });
        } else {
            setFormData({
                full_name: '',
                email: '',
                mobile: '',
                usr_pwd: '',
                confirmPassword: '',
                work_assign: []
            });
        }

        setFormErrors({});
        setShowModal(true);
    };

    // Close modal
    const closeModal = () => {
        setShowPassword(false);
        setShowConfirmPassword(false);
        setShowModal(false);
        setCurrentUser(null);
        setFormData({
            full_name: '',
            email: '',
            mobile: '',
            usr_pwd: '',
            confirmPassword: '',
            work_assign: []
        });
        setFormErrors({});
    };

    // Handle form submit
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setBtnLoading(true);

        const userPayload = {
            full_name: formData.full_name,
            email: formData.email,
            mobile: formData.mobile,
            work_assign: formData.work_assign.join(','),
            is_active: 1,
            assign: 1
        };

        if (modalType === 'add') {
            userPayload.usr_pwd = formData.usr_pwd;
        }

        try {
            let response;
            if (modalType === 'add') {
                response = await apiService.addNewUser(userPayload);
            } else {
                response = await apiService.UpdateNewUser(currentUser.usr_id, userPayload);
            }

            if (response.status === 200) {
                toast.success(`User ${modalType === 'add' ? 'added' : 'updated'} successfully`);
                closeModal();
                getSubUsers();
            } else {
                toast.error(`Failed to ${modalType} user`);
            }
        } catch (error) {
            toast.error(`Error ${modalType === 'add' ? 'adding' : 'updating'} user`);
        } finally {
            setBtnLoading(false);
        }
    };

    if (loadingSubUsers) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex justify-center items-center h-80">
                    <div className="text-center animate-float">
                        <div className="inline-flex relative">
                            <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
                            <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
                        </div>
                        <p className="text-gray-400 mt-4">{translations[language].subUserLoader}</p>
                    </div>
                </div>
            </div>
        );
    }

    const subUsersData = () => (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="overflow-x-auto">
                <table className="border border-gray-300 w-full text-base">
                    <thead>
                        <tr className="bg-gray-100 text-gray-700 text-center">
                            <th className="border p-3 font-semibold w-20">{translations[language].Action}</th>
                            <th className="border p-3 font-semibold">{translations[language].name}</th>
                            <th className="border p-3 font-semibold">{translations[language].email}</th>
                            <th className="border p-3 font-semibold">{translations[language].contactNo}</th>
                            <th className="border p-3 font-semibold">{translations[language].work}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {subUsers.length > 0 ? (
                            subUsers.map((item) => (
                                <tr
                                    key={item.usr_id}
                                    className="hover:bg-gray-50 transition-colors"
                                >
                                    <td className="border p-3">
                                        <button
                                            onClick={() => openModal('edit', item)}
                                            className="text-blue-600 hover:text-blue-800"
                                        >
                                            <Pencil size={18} />
                                        </button>
                                    </td>

                                    <td className="border p-3 font-medium">{item.full_name}</td>
                                    <td className="border p-3">{item.email}</td>
                                    <td className="border p-3">{item.mobile}</td>

                                    <td className="border p-3">
                                        {item.work_names ? (
                                            <div className="flex flex-col gap-1">
                                                {item.work_names
                                                    .split(',')
                                                    .map((work) => work.trim())
                                                    .map((work, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm font-medium"
                                                        >
                                                            {work}
                                                        </span>
                                                    ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center p-5 text-gray-500">
                                    No subUsers Found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <>
            <div className="bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto p-4">
                    {/* Header */}
                    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                        <div className="flex justify-between items-center">

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => openModal('add')}
                                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                                >
                                    <UserPlus className="mr-2 " size={16} />{translations[language].addSubUser}
                                </button>

                            </div>
                        </div>
                    </div>

                    {/* Main Document Display */}
                    {subUsersData()}

                </div>
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-semibold">
                                    {modalType === 'add' ? translations[language].addSubUser : translations[language].editSubUser}
                                </h2>
                                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">{translations[language].name} *</label>
                                    <input
                                        type="text"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.full_name ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder={translations[language].EnterName}
                                        autoComplete="off"
                                    />
                                    {formErrors.full_name && <p className="text-red-500 text-xs mt-1">{formErrors.full_name}</p>}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">{translations[language].Email} *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder={translations[language].Enteremailaddress}
                                        autoComplete="off"
                                    />
                                    {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                                </div>

                                {/* Mobile */}
                                <div>
                                    <label className="block text-sm font-medium mb-1">{translations[language].contactNo} *</label>
                                    <input
                                        type="text"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        maxLength="10"
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.mobile ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder={translations[language].Entermobilenumber}
                                        autoComplete="off"
                                    />
                                    {formErrors.mobile && <p className="text-red-500 text-xs mt-1">{formErrors.mobile}</p>}
                                </div>

                                {/* Password - only for add */}
                                {modalType === 'add' && (
                                    <>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                {translations[language].Password} *
                                            </label>

                                            <div className="relative">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    name="usr_pwd"
                                                    value={formData.usr_pwd}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-3 py-2 border rounded-lg pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.usr_pwd ? "border-red-500" : "border-gray-300"
                                                        }`}
                                                    placeholder={translations[language].enterPassword}
                                                    autoComplete="new-password"
                                                />

                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                                >
                                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>

                                            {formErrors.usr_pwd && (
                                                <p className="text-red-500 text-xs mt-1">{formErrors.usr_pwd}</p>
                                            )}
                                        </div>


                                        <div>
                                            <label className="block text-sm font-medium mb-1">
                                                {translations[language].ConfirmPassword} *
                                            </label>

                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="confirmPassword"
                                                    value={formData.confirmPassword}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-3 py-2 border rounded-lg pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.confirmPassword ? "border-red-500" : "border-gray-300"
                                                        }`}
                                                    placeholder={translations[language].enterConfirmPassword}
                                                    autoComplete="new-password"
                                                />

                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                                                >
                                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                                </button>
                                            </div>

                                            {formErrors.confirmPassword && (
                                                <p className="text-red-500 text-xs mt-1">
                                                    {formErrors.confirmPassword}
                                                </p>
                                            )}
                                        </div>

                                    </>
                                )}

                                {/* Work Assignment */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">{translations[language].workAssign}</label>
                                    <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto">
                                        {workAssignments.length > 0 ? (
                                            workAssignments.map((work) => (
                                                <div key={work.assign_id} className="flex items-center mb-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`work-${work.assign_id}`}
                                                        checked={formData.work_assign.includes(work.assign_id)}
                                                        onChange={() => handleWorkAssignChange(work.assign_id)}
                                                        className="mr-2"
                                                    />
                                                    <label htmlFor={`work-${work.assign_id}`} className="text-sm cursor-pointer">
                                                        {work.name || work.work_name || `Work ${work.assign_id}`}
                                                    </label>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-gray-500 text-sm">{translations[language].noWorkAssignmentsAvailable}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={handleSubmit}
                                    disabled={btnLoading}
                                    className={`px-4 py-2 rounded-lg text-white transition-colors flex items-center justify-center ${btnLoading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                                        }`}
                                >
                                    {btnLoading ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        modalType === 'add' ? translations[language].addUser : translations[language].EditUser
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserAndRoleManagement;
